import { CheckIcon } from 'lucide-react';
import { CopyIcon } from 'lucide-react';
import { GitCommitVerticalIcon } from 'lucide-react';
import { GitMergeIcon } from 'lucide-react';
import { useRef } from 'react';
import { useState } from 'react';

import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';

import { GithubCommit } from '@/features/pulls/services/pulls';
import { getTimeAgo } from '@/lib/time';

export function CopyableCommit({ commit, merged = false }: { commit: GithubCommit; merged?: boolean }) {
  const [copiedSha, setCopiedSha] = useState<string | null>(null);
  const [isHovering, setIsHovering] = useState(false);
  const tooltipTimeoutRef = useRef<number | null>(null);

  const isCopied = copiedSha === commit.sha;
  const isVerified = commit.commit?.verification?.verified === true;
  const authorName = commit.author?.login || commit.commit?.author?.name || 'Unknown';

  const copyToClipboard = (sha: string) => {
    navigator.clipboard.writeText(sha);
    setCopiedSha(sha);

    // Clear existing timeout if there is one
    if (tooltipTimeoutRef.current) {
      window.clearTimeout(tooltipTimeoutRef.current);
    }

    // Set timeout to close tooltip
    tooltipTimeoutRef.current = window.setTimeout(() => {
      // setTooltipOpen(null);
      // Reset copied state after tooltip closes
      setTimeout(() => {
        setCopiedSha(null);
      }, 200);
    }, 1500);
  };

  return (
    <div
      key={commit.sha}
      className="flex items-center gap-3 p-2 rounded-md hover:bg-accent/50 cursor-pointer relative group"
      onClick={() => copyToClipboard(commit.sha)}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
      title="Click to copy commit SHA"
    >
      <div className="text-gray-600 bg-gray-100 rounded-sm size-7 flex items-center justify-center shrink-0 overflow-hidden relative">
        {merged ? (
          <GitMergeIcon
            size={18}
            className={`text-purple-500 bg-purple-100 rounded-sm absolute transition-opacity duration-50 ${!isHovering && !isCopied ? 'opacity-100' : 'opacity-0'}`}
          />
        ) : (
          <GitCommitVerticalIcon
            size={18}
            className={`absolute transition-opacity duration-50 ${!isHovering && !isCopied ? 'opacity-100' : 'opacity-0'}`}
          />
        )}
        <CopyIcon
          size={18}
          className={`absolute transition-opacity duration-50 ${isHovering && !isCopied ? 'opacity-100' : 'opacity-0'}`}
        />
        <CheckIcon
          size={18}
          className={`absolute transition-opacity duration-50 text-green-600 ${isCopied ? 'opacity-100' : 'opacity-0'}`}
        />
      </div>

      <div className="flex items-center gap-2 flex-1">
        <div className="relative">
          <Avatar className="size-5">
            {commit.author?.avatar_url ? (
              <AvatarImage src={commit.author.avatar_url} alt={authorName} />
            ) : (
              <AvatarFallback>{authorName.substring(0, 2) || '??'}</AvatarFallback>
            )}
          </Avatar>
          {isVerified && (
            <div className="absolute -bottom-0.5 -right-0.5 rounded-full bg-green-100 p-[2px] border border-background dark:border-card">
              <CheckIcon className="size-1.5 text-green-600" />
            </div>
          )}
        </div>
        <div className="min-w-0">
          <p className="text-sm font-light text-muted-foreground text-ellipsis overflow-hidden">
            {merged
              ? `${commit.author?.login || 'Someone'} merged this PR`
              : `${commit.commit?.message?.split('\n')[0] || 'No commit message'}`}
          </p>
        </div>
      </div>

      {/* {!isCopied && ( */}
      <span className="text-xs text-muted-foreground whitespace-nowrap">
        {commit.commit?.author?.date ? getTimeAgo(commit.commit.author.date) : 'recently'}
      </span>
      {/* )} */}

      {isCopied && (
        <div className="absolute right-0 top-1/2 transform -translate-y-1/2 bg-green-100 text-green-800 text-xs rounded px-2 py-0.5 font-medium animate-in fade-in duration-150">
          SHA copied!
        </div>
      )}
    </div>
  );
}
